import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { config } from '@uhg-abyss/web/tools/config';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Footer } from '@uhg-abyss/web/ui/Footer';
import { Link } from '@uhg-abyss/web/ui/Link';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getCoverageTypeString,
  getGuestPlanSelectionData,
  getYearIndex,
} from '../../frontends/GuestExperience/Guest.utils';
import {
  getClientPortalConfig,
  getGuestThemeColors,
} from '../../frontends/GuestExperience/utils';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useFpcGuestToken } from '../../hooks/useFpcGuestToken';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { ClientNames, Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { getFeatureFlag } from '../Utils/getFeatureFlag';
import { FooterColumn, SubFooterLink, getFooterData } from './utils';

export const GuestFooter = () => {
  const { navigate } = useRouter();

  // Feature flag: Lagoon > psx-web-transperfect-guest-plan-selection > feature-flags > PDF_PRINT_DIRECTORY_FEATURE
  const lagoonFeatureFlagsTable = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_FLAGS
  );
  const featureFlags: { key: string; active: boolean }[] =
    typeof lagoonFeatureFlagsTable === 'function'
      ? lagoonFeatureFlagsTable()
      : lagoonFeatureFlagsTable || [];
  const isPrintDirectoryEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.PDF_PRINT_DIRECTORY_FEATURE
  ); // TODO: remove after verifying Lagoon cache is refreshed

  const [section, setSection] = useState<FooterColumn[]>([]);
  const [subFooterLinks, setSubFooterLinks] = useState<SubFooterLink[]>([]);
  const configuration = (useLagoon('footer-configuration') as Function)();
  const fpcConfiguration = (useLagoon('fpc-token-config') as Function)();
  const FpcTokenSearchDirectoryTypes = [
    ...new Set(fpcConfiguration?.map((row: any) => row.searchDirectory) || []),
  ];
  const configurationKey = JSON.stringify(configuration);
  const { t } = useTranslation();
  const sectionLabel = t('SECTION');
  const generalLabel = t('GENERAL');
  const { selectedPlan = {}, coverageTypeCode: coverageType = '' } =
    getGuestPlanSelectionData();
  const { planYear = '' } =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.GUEST_USER) || {};

  const clientName = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.CLIENT_NAME
  );

  // GEHA theme detection using centralized config
  const clientPortalConfig = getClientPortalConfig(clientName);
  const theme = getGuestThemeColors(clientPortalConfig.themeOverride);
  const isGehaGuest = clientName?.toLowerCase() === ClientNames.GEHA;
  const footerBgColor = isGehaGuest ? 'transparent' : theme.primaryColor;

  const [, getFpcGuestToken] = useFpcGuestToken({});
  const { stateCode, zipCode } = useGeoLocationStorage();
  const fipscode = stateCode || sessionStorage.getItem('countyFipsCode') || '';
  const url = window.location.href;
  const showPrint = url.includes('/guest') && url.includes('/browse');
  const FPC_BASE_URL = config('FPC_BASE_URL');

  const navigateToFpcGuest = async () => {
    const language = getLanguage()?.code;
    const yearIndex = getYearIndex(selectedPlan?.years, planYear);
    const yearObj = selectedPlan?.years?.[yearIndex];

    const planIdentifier =
      typeof selectedPlan?.planIdentifier === 'string'
        ? selectedPlan?.planIdentifier.replace(/-/g, '')
        : '';

    const isLagoonDriven =
      FpcTokenSearchDirectoryTypes.includes(selectedPlan?.searchDirectory) &&
      fpcConfiguration?.find(
        (row: any) =>
          row.searchDirectory === selectedPlan?.searchDirectory &&
          row.planIdentifier.split(',').includes(planIdentifier)
      );
    const token = await getFpcGuestToken({
      variables: {
        planName: selectedPlan?.planName,
        delsys: isLagoonDriven ? isLagoonDriven.fpcDelsysCode : planIdentifier,
        coverageType: getCoverageTypeString(coverageType),
        language: language || 'en',
        clusterKey: yearObj?.clusterKey || '',
        fipsCode: fipscode,
        partnerId:
          clientName?.toLowerCase() === ClientNames.EMPIRE
            ? 'uhc.empire'
            : isLagoonDriven
            ? isLagoonDriven.partnerId
            : 'uhc',
      },
    }).then(
      async (response: { data: { getFpcGuestToken: { token: string } } }) =>
        response?.data?.getFpcGuestToken?.token || ''
    );

    const printUrl = `${FPC_BASE_URL}/guest/${token}?planyear=${planYear}&isDirectoryPrint=true&zipCode=${zipCode}`;
    window.open(printUrl, '_blank');
  };

  useEffect(() => {
    const footerData: ReturnType<typeof getFooterData> = getFooterData(
      configuration,
      sectionLabel,
      generalLabel,
      getLanguage()?.code === 'es',
      clientName || ''
    );
    setSection(footerData?.section);

    const subLinks: SubFooterLink[] = footerData?.general?.[0]?.links.map(
      (link) => ({
        label: link?.name,
        href: link?.url || '#',
      })
    );

    setSubFooterLinks(subLinks);
  }, [configurationKey, sectionLabel, generalLabel]);

  return (
    <Box
      css={{
        'abyss-box-root': {
          backgroundColor: isGehaGuest ? '#FAFAFA' : 'white',
          width: '100%',
          height: 'auto',
          opacity: 1,
          fontFamily: '$primary',
          padding: '0px',
        },
      }}
    >
      {!isGehaGuest && (
        <Footer
          css={{
            'abyss-footer-container': {
              alignItems: 'center',
              padding: '24px 16px 32px 16px',
              backgroundColor: footerBgColor,
            },
          }}
          data-testid="guest-footer"
          disableDefaultProviderProps={true}
          fullWidth
        >
          <Flex
            css={{
              width: '100%',
              justifyContent: 'center',
              paddingBottom: '8px',
            }}
          >
            <Flex
              css={{
                maxWidth: '1244px',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {section?.length > 0 &&
                section.map((column, i) => (
                  <Footer.Section
                    css={{
                      'abyss-footer-section-title': {
                        marginBottom: '8px',
                      },
                      'abyss-footer-link-item': {
                        textDecoration: 'none',
                      },
                      'abyss-footer-list-container': {
                        gap: '4px',
                      },
                      'abyss-footer-section': {
                        width: '33%',
                        '@media (max-width: 744px)': {
                          marginTop: i === 0 ? '0px' : '32px',
                          width: '100%',
                        },
                      },
                    }}
                    data-testid="footer-heading"
                    key={`footer-column-${i}`}
                    title={column?.header}
                  >
                    {column.links.map((link, j) => (
                      <Footer.Link
                        css={{
                          'abyss-footer-link': {
                            textDecoration: 'none',
                            display:
                              link.url === '/link'
                                ? showPrint
                                  ? 'flex'
                                  : 'none'
                                : 'flex',
                            lineHeight: 1.4,
                            color: 'white !important',
                          },
                        }}
                        data-testid="footer-links"
                        href={link.url}
                        key={`footer-link-${i}-${j}`}
                        onClick={(e) => {
                          if (link.url === '/link') {
                            e.preventDefault();
                            if (isPrintDirectoryEnabled) {
                              navigate('/print-directory');
                            } else {
                              navigateToFpcGuest();
                            }
                          }
                        }}
                        openNewWindow
                      >
                        {link.name}
                      </Footer.Link>
                    ))}
                  </Footer.Section>
                ))}
            </Flex>
          </Flex>
          <Footer.SubFooter
            css={{
              'abyss-footer-copyright': {
                display: 'none',
              },
              'abyss-footer-sub-footnotes': {
                justifyContent: 'flex-start',
                display: 'block',
              },
              'abyss-footer-sub-link': {
                fontSize: '12px',
                color: 'white !important',
              },
              'abyss-footer-sub-list': {
                justifyContent: 'flex-end',
                flexDirection: 'row',
                gap: '8px',
              },
              'abyss-footer-sub-wrapper': {
                width: '100%',
                maxWidth: '1244px',
                paddingTop: '32px',
                backgroundColor: footerBgColor,
              },
            }}
            data-testid="guest-sub-footer"
            links={subFooterLinks}
          />
        </Footer>
      )}
      {isGehaGuest && subFooterLinks?.length > 0 && (
        <Flex
          css={{
            flexDirection: 'column',
            alignItems: 'center',
            borderTop: '0.5px solid #CAD9E2',
            backgroundColor: '#FAFAFA',
          }}
          data-testid="geha-sub-footer-links"
        >
          <Flex
            css={{
              justifyContent: 'center',
              padding: '16px',
              flexWrap: 'wrap',
              '@media (max-width: 744px)': {
                justifyContent: 'flex-end',
              },
            }}
          >
            {subFooterLinks.map((link, index) => (
              <Flex
                alignItems="center"
                css={{
                  '&::before':
                    index > 0
                      ? {
                          content: '"|"',
                          margin: '0 8px',
                          color: '#C5C5C5',
                          fontSize: '11px',
                          fontStyle: 'normal',
                          fontWeight: 600,
                          lineHeight: '20px',
                        }
                      : {},
                }}
                key={`geha-sub-link-${index}`}
              >
                <Link
                  css={{
                    'abyss-link-root': {
                      color: '#424242',
                      textAlign: 'center',
                      fontSize: '11px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '20px',
                      textDecoration: 'none',
                    },
                  }}
                  data-testid={`geha-sub-footer-link-${index}`}
                  hideIcon
                  href={link.href}
                  openNewWindow
                >
                  {link.label}
                </Link>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
    </Box>
  );
};

export default GuestFooter;
